import type { Writable } from "svelte/store";
import type { GcsConfig } from "./fs";
import { FS } from "./fs";
import { s3SanitizeDir, s3SanitizePath } from "./fs-s3";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Google Cloud Storage sends no CORS headers by default, so calls go through
// /api/gcs. The service account key file is a request header.

class FileSystemGcs extends FS {
  constructor(config: GcsConfig) {
    super(config);
  }

  get gcsConfig(): GcsConfig {
    return this.config as GcsConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await gcsList(this.gcsConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let url = gcsURL(this.gcsConfig, "/api/gcs/getpublicurl", "path", path);
    let js = await gcsCall(this.gcsConfig, url);
    if (!js.url) {
      throw new Error("gcs did not return a url");
    }
    return mkURL("https://workers.filerion.com/api/fetch", "url", js.url);
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    path = s3SanitizePath(path);
    let url = gcsURL(this.gcsConfig, "/api/gcs/upload", "path", path);
    await gcsSend("PUT", url, this.gcsConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = gcsURL(this.gcsConfig, "/api/gcs/rename", "path", path, "newPath", newPath);
    await gcsCall(this.gcsConfig, url);
  }

  async createDirectory(path: string): Promise<void> {
    let url = gcsURL(this.gcsConfig, "/api/gcs/createdirectory", "path", path);
    await gcsCall(this.gcsConfig, url);
  }

  async deleteFile(path: string): Promise<void> {
    path = s3SanitizePath(path);
    let url = gcsURL(this.gcsConfig, "/api/gcs/deletefile", "path", path);
    await gcsCall(this.gcsConfig, url);
  }
}

export async function testGcsConnection(config: GcsConfig): Promise<{ email: string }> {
  let js = await gcsCall(config, gcsURL(config, "/api/gcs/test"));
  return { email: js.email || "" };
}

async function gcsList(config: GcsConfig, dir: string): Promise<any[][]> {
  dir = s3SanitizeDir(dir);
  let js = await gcsCall(config, gcsURL(config, "/api/gcs/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("gcs list failed");
  }
  return js;
}

function gcsURL(config: GcsConfig, path: string, ...args: string[]): string {
  return mkURL(path, "bucket", config.bucket, ...args);
}

// The key file is JSON with newlines, so it travels as base64.
function encodeKeyFile(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function gcsHeaders(config: GcsConfig): Record<string, string> {
  return {
    "X-Gcs-Key": encodeKeyFile(config.key),
  };
}

async function gcsCall(config: GcsConfig, url: string): Promise<any> {
  let rsp = await fetch(url, { headers: gcsHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `gcs ${rsp.status}`);
  }
  return js;
}

function gcsSend(
  method: string,
  url: string,
  config: GcsConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
): Promise<void> {
  let headers = gcsHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(gcsErrText(xhr)));
    };
    xhr.onerror = () => {
      reject(new Error("gcs request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      xhr.upload.onprogress = (ev) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

function gcsErrText(xhr: XMLHttpRequest): string {
  let text = xhr.responseText || "";
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `gcs ${xhr.status}`;
}

export function makeFileSystemGcs(config: GcsConfig): FileSystemGcs {
  return new FileSystemGcs(config);
}
